import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ItemCard from "../components/ItemCard";
import { ContentApi } from "../../../../shared/api/contentApi";
import { sortItems } from "../../../../shared/helpers/SortByName";
import PaginationFooter from "../../../../components/ui/PaginationFooter";
import NewItemDialog from "../components/NewItemDialog";
import { useContentContext } from "../../ContentContext";
import { notify } from "shared/utils/notify";
import { resolvePagedTotals } from "../../../../shared/helpers/pagination";

import { Flex, Select, Button, Modal, Input } from "antd";

const SORT_FIELDS = [
    { value: "name", label: "Имя" },
    { value: "systemName", label: "System Name" },
];

export default function ItemsPage() {
    const navigate = useNavigate();
    const { canMutate, selectedVersion, projectId, versionId } = useContentContext();
    const [createOpen, setCreateOpen] = useState(false);
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    // серверная пагинация
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // когда меняется фильтр/поиск — сбрасываем страницу на 1
    useEffect(() => {
        setPage(1);
    }, [q, pageSize]);

    useEffect(() => {
        if (!projectId || !versionId) {
            setItems([]);
            setTotal(0);
            setTotalPages(1);
            setLoading(false);
            return;
        }

        const ac = new AbortController();
        setLoading(true);
        setError("");

        ContentApi.getItems(
            {
                search: q || undefined,
                page,
                pageSize,
            },
            { signal: ac.signal }
        )
            .then((data) => {
                setItems(Array.isArray(data?.items) ? data.items : []);
                const paging = resolvePagedTotals(data, pageSize);
                setTotal(paging.total);
                setTotalPages(paging.totalPages);
            })
            .catch((e) => {
                if (!ac.signal.aborted) setError(e.message || "Ошибка загрузки");
            })
            .finally(() => !ac.signal.aborted && setLoading(false));

        return () => ac.abort();
    }, [q, page, pageSize, projectId, versionId]);

    // сортируем ТОЛЬКО текущую страницу (если серверной сортировки нет)
    const pageItems = useMemo(
        () => sortItems(items, sortBy, sortDir),
        [items, sortBy, sortDir]
    );

    const safePage = Math.min(page, totalPages);

    return (
        <section className="p-4 md:p-6 space-y-4">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">
                        Предметы
                    </h1>
                    <p className="text-sm text-zinc-200">
                        Данные с SbContentAPI
                    </p>
                </div>
                <button
                    className="btn btn-soft-accent"
                    type="button"
                    onClick={() => navigate(`/content/entity`)}
                >
                    Таблица сущностей
                </button>
                <button
                    className="btn btn-soft-accent"
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    disabled={!projectId || !versionId}
                >
                    Создать предмет
                </button>
                <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="item-search">
                            Поиск
                        </label>
                        <Input
                            id="item-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Имя или systemName…"
                            name="item-search"
                            className="input w-full"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="item-sort-by">
                            Сортировать по
                        </label>
                        <Select
                            id="item-sort-by"
                            value={sortBy}
                            onChange={setSortBy}
                            className="w-full"
                        >
                            {SORT_FIELDS.map((f) => (
                                <Select.Option key={f.value} value={f.value}>
                                    {f.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="item-sort-dir">
                            Порядок
                        </label>
                        <Select
                            id="item-sort-dir"
                            value={sortDir}
                            onChange={setSortDir}
                            className="w-64"
                        >
                            <Select.Option value="asc">По возрастанию</Select.Option>
                            <Select.Option value="desc">По убыванию</Select.Option>
                        </Select>
                    </div>
                    <div className="flex flex-col">
                        <label className="eyebrow mb-1" htmlFor="item-page-size">
                            На странице
                        </label>
                        <Select
                            id="item-page-size"
                            value={String(pageSize)}
                            onChange={(val) => setPageSize(Number(val))}
                            className="w-64"
                        >
                            {[6, 12, 18, 24].map((n) => (
                                <Select.Option key={n} value={String(n)}>
                                    {n}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </header>

            <NewItemDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(created) => {
                    const newId = created?.id || created?.itemId || created?.ItemId;
                    if (newId) {
                        navigate(`/content/entity/${newId}`);
                    } else {
                        setPage(1);
                    }
                }}
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            )}

            {!canMutate && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 p-3 text-sm">
                    Текущая версия ({selectedVersion?.status || "unknown"}) доступна только для чтения. Используйте Fork в draft для изменений.
                </div>
            )}

            {loading ? (
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {pageItems.map((item) => (
                            <ItemCard
                                key={item.id}
                                item={item}
                                onOpen={(it) => navigate(`/content/entity/${it.id}`)}
                                onCopy={(it) => notify.wip(`Копирование ${it.systemName}`)}
                            />
                        ))}
                    </div>
                    <PaginationFooter
                        total={total}
                        page={safePage}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />
                </>
            )}
        </section>
    );
}